/**
 * useSupabaseReachability
 * ------------------------------------------------------------------
 * Lightweight Supabase backend reachability probe for page-load and
 * background health checks. Surfaces a clear "unreachable" signal so
 * the UI can warn users when login / data fetches will hang.
 *
 * Design rules:
 * - Zero hallucination: only reports "unreachable" after a real network
 *   failure or HTTP 5xx / timeout against the Supabase auth metadata
 *   endpoint (the same endpoint AuthContext relies on).
 * - Non-invasive: uses `fetch` with AbortController timeout. Does NOT
 *   call any RLS-protected table, does NOT require an authenticated
 *   session, does NOT mutate data.
 * - Browser offline (navigator.onLine === false) is reported as
 *   `offline`, distinct from `unreachable` (network up, Supabase down).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { safeIsOnline } from '@/lib/browserSafeUtils';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

export type ReachabilityStatus = 'checking' | 'reachable' | 'unreachable' | 'offline';

export interface ReachabilityState {
  status: ReachabilityStatus;
  lastCheckedAt: number | null;
  lastError: string | null;
  recheck: () => void;
}

const PING_TIMEOUT_MS = 6000;
const POLL_INTERVAL_MS = 60_000;

async function pingSupabase(): Promise<{ ok: boolean; error?: string }> {
  if (!SUPABASE_URL || !SUPABASE_ANON) {
    return { ok: false, error: 'Supabase env vars missing' };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);

  try {
    // Auth settings endpoint is public, cheap, and returns JSON when
    // the project is reachable. It does not require a session.
    const res = await fetch(`${SUPABASE_URL}/auth/v1/settings`, {
      method: 'GET',
      headers: {
        apikey: SUPABASE_ANON,
        Authorization: `Bearer ${SUPABASE_ANON}`,
      },
      signal: controller.signal,
      cache: 'no-store',
    });

    if (res.ok) return { ok: true };
    // 5xx or unexpected status => treat as unreachable
    return { ok: false, error: `HTTP ${res.status}` };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'network error';
    return { ok: false, error: message };
  } finally {
    clearTimeout(timer);
  }
}

export function useSupabaseReachability(): ReachabilityState {
  const [status, setStatus] = useState<ReachabilityStatus>('checking');
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null);
  const inFlightRef = useRef(false);

  const runCheck = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    if (!safeIsOnline()) {
      setStatus('offline');
      setLastError('Browser is offline');
      setLastCheckedAt(Date.now());
      inFlightRef.current = false;
      return;
    }

    const result = await pingSupabase();
    setLastCheckedAt(Date.now());
    if (result.ok) {
      setStatus('reachable');
      setLastError(null);
    } else {
      setStatus('unreachable');
      setLastError(result.error ?? 'unknown error');
    }
    inFlightRef.current = false;
  }, []);

  useEffect(() => {
    // Initial probe on mount
    runCheck();

    // Re-check on browser network transitions
    const handleOnline = () => runCheck();
    const handleOffline = () => {
      setStatus('offline');
      setLastError('Browser is offline');
      setLastCheckedAt(Date.now());
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Background poll
    const interval = window.setInterval(runCheck, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.clearInterval(interval);
    };
  }, [runCheck]);

  return { status, lastError, lastCheckedAt, recheck: runCheck };
}
